import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../lib/api";
import { PageHeader } from "../components/shared/PageHeader";
import { AdminTable } from "../components/shared/AdminTable";
import { SlideOver } from "../components/shared/SlideOver";
import { useForm } from "react-hook-form";
import { useAdminT } from "../../../hooks/useAdminT";

interface Inquiry {
    id: string;
    name: string;
    email: string;
    phone: string;
    message: string;
    status: string;
    createdAt: string;
}

export default function InquiriesPage() {
    const { t } = useAdminT();
    const queryClient = useQueryClient();
    const [isDrawOpen, setIsDrawOpen] = useState(false);
    const [selectedInquiry, setSelectedInquiry] = useState<Inquiry | null>(null);

    const { register, handleSubmit, reset } = useForm();

    const { data: inquiries, isLoading } = useQuery({
        queryKey: ["inquiries"],
        queryFn: () => api.get("/api/admin/inquiries").then(res => res.data.data)
    });

    const updateMutation = useMutation({
        mutationFn: (data: any) => api.put(`/api/admin/inquiries/${selectedInquiry?.id}`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["inquiries"] });
            handleClose();
        }
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => api.delete(`/api/admin/inquiries/${id}`),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["inquiries"] })
    });

    const handleClose = () => {
        setIsDrawOpen(false);
        setSelectedInquiry(null);
        reset();
    };

    const handleView = (inquiry: Inquiry) => {
        setSelectedInquiry(inquiry);
        reset({ status: inquiry.status });
        setIsDrawOpen(true);
    };

    const onSubmit = (data: any) => {
        updateMutation.mutate(data);
    };

    return (
        <div className="p-8">
            <PageHeader
                title={t("inquiries.title")}
                description={t("inquiries.subtitle")}
            />

            <AdminTable
                data={inquiries}
                isLoading={isLoading}
                columns={[
                    { header: t("inquiries.fields.name"), accessorKey: "name", className: "font-medium" },
                    { header: t("inquiries.fields.email"), accessorKey: "email" },
                    { header: t("inquiries.fields.phone"), accessorKey: "phone" },
                    {
                        header: t("common.status"),
                        cell: (i) => (
                            <span className={`text-xs px-2 py-1 rounded font-semibold ${i.status === 'NEW' ? 'bg-blue-100 text-blue-800' :
                                i.status === 'CONTACTED' ? 'bg-green-100 text-green-800' :
                                    'bg-gray-100 text-gray-800'
                                }`}>
                                {t(`status.${i.status.toLowerCase()}`)}
                            </span>
                        )
                    },
                    { header: t("common.createdAt"), cell: (i) => new Date(i.createdAt).toLocaleDateString() },
                ]}
                onEdit={handleView}
                onDelete={(i) => {
                    if (confirm(t("common.confirmProceed"))) deleteMutation.mutate(i.id);
                }}
            />

            <SlideOver
                isOpen={isDrawOpen}
                onClose={handleClose}
                title={t("inquiries.title")}
            >
                {selectedInquiry && (
                    <div className="space-y-6">
                        <div className="bg-slate-50 p-4 rounded border border-slate-100">
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                    <p className="text-slate-500">{t("inquiries.fields.name")}</p>
                                    <p className="font-medium">{selectedInquiry.name}</p>
                                </div>
                                <div>
                                    <p className="text-slate-500">{t("common.createdAt")}</p>
                                    <p className="font-medium">{new Date(selectedInquiry.createdAt).toLocaleString()}</p>
                                </div>
                                <div>
                                    <p className="text-slate-500">{t("inquiries.fields.email")}</p>
                                    <p className="font-medium">{selectedInquiry.email}</p>
                                </div>
                                <div>
                                    <p className="text-slate-500">{t("inquiries.fields.phone")}</p>
                                    <p className="font-medium">{selectedInquiry.phone}</p>
                                </div>
                            </div>
                        </div>

                        <div>
                            <p className="text-sm font-medium text-slate-700 mb-2">{t("inquiries.fields.message")}</p>
                            <div className="p-4 border rounded bg-white text-sm text-slate-600 min-h-[100px]">
                                {selectedInquiry.message}
                            </div>
                        </div>

                        <form onSubmit={handleSubmit(onSubmit)} className="border-t pt-6 mt-6">
                            <label className="block text-sm font-medium mb-2">{t("inquiries.fields.status")}</label>
                            <div className="flex gap-2">
                                <select {...register("status")} className="flex-1 px-3 py-2 border rounded-md">
                                    <option value="NEW">{t("status.new")}</option>
                                    <option value="CONTACTED">{t("status.contacted")}</option>
                                    <option value="CLOSED">{t("status.closed")}</option>
                                </select>
                                <button type="submit" className="px-4 py-2 bg-secondary text-white rounded hover:bg-primary-dark">
                                    {t("common.save")}
                                </button>
                            </div>
                        </form>
                    </div>
                )}
            </SlideOver>
        </div>
    );
}
